import { Button, Divider, Grid, Typography } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import { ActionButton } from '../../Button';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { useUserContext } from '../../../contexts/UserContext';
import IGovernanceItem from '../../../interfaces/IGovernanceItem';
import useForceRender from '../../../hooks/useForceRender';
import DefaultConstants from '../../../data/Constants';
import GovernanceItemDescriptionDialog from './GovernanceItemDescriptionDialog';
import { GovernanceStatusData, GovernanceStatusEnum } from '../../../data/Enums';
import DateTimeHelper from '../../../helpers/DateTimeHelper';
import GovernanceItemsList from '../../GovernanceItemsList';
import { MoreHoriz } from '@mui/icons-material';


interface IGovernanceItemsDialogProps extends IDefaultDialogProps {
    governanceItems: IGovernanceItem[];
    room: IRoom;
    hasPendingGovernanceItems: (value: boolean) => void;
}


const governanceStatusInspection = GovernanceStatusData[GovernanceStatusEnum.Inspection];

const GovernanceItemsDialog: React.FC<IGovernanceItemsDialogProps> = ({
    open,
    handleClose,
    governanceItems,
    room,
    hasPendingGovernanceItems
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();

    const forceRender = useForceRender();

    const [checkedItems, setCheckedItems] = useState<number[]>([]);
    const [selectedItem, setSelectedItem] = useState<IGovernanceItem | undefined>(undefined);


    const dialogContentProps = useMemo(() => {
        const contentProps = {
            title: 'governanceItems',
            requiredItems: true,
            governanceItemsExecuted: [] as number[],
            beforeGovernanceItemsComponent: [] as JSX.Element[],
        };

        if (room.governanceStatusId === governanceStatusInspection.partnerId) {
            contentProps.title = 'housekeepingDetails';
            contentProps.requiredItems = false;
            contentProps.governanceItemsExecuted = (room.roomLog?.governanceItems ?? []).map(i => +i.id);

            contentProps.beforeGovernanceItemsComponent.push(<Grid item md={8} xs={8}>
                <Typography variant='caption' className='fontsize-10 text-color-2'>
                    {translate('executedBy')}
                </Typography>
    
                <Typography variant='body2'>
                    {room.roomLog?.user.fullName ?? '-'}
                </Typography>
            </Grid>);
    
            contentProps.beforeGovernanceItemsComponent.push(<Grid item md={4} xs={4}>
                <Typography variant='caption' className='fontsize-10 text-color-2'>
                    {translate('executedAt')}
                </Typography>
    
                <Typography variant='body2'>
                    {room.roomLog?.executionTime ? DateTimeHelper.ToElapsedTime(room.roomLog.executionTime) : '-'}
                </Typography>
            </Grid>);
    
            contentProps.beforeGovernanceItemsComponent.push(<Grid item xs={12}>
                <Typography variant='caption' className='fontsize-10 text-color-2'>
                    {translate('observations')}
                </Typography>
    
                <Typography variant='body2'>
                    {room.roomLog?.observations && room.roomLog.observations.trim().length > 0 ? room.roomLog.observations : '-'}
                </Typography>
            </Grid>);
    
            contentProps.beforeGovernanceItemsComponent.push(<Grid item xs={12} marginY={1}>
                <Divider />
            </Grid>);
    
            contentProps.beforeGovernanceItemsComponent.push(<Grid item xs={12}>
                <Typography variant='subtitle2' className='fontweight-600'>{translate('governanceItems')}:</Typography>
            </Grid>);
        }

        return contentProps;
        // eslint-disable-next-line
    }, [room, translate]);


    useEffect(() => {
        setCheckedItems([]);
        handleCheckingHasPendingItems([]);
        // eslint-disable-next-line
    }, [room]);


    const handleUpdateCheckedItems = useCallback((itemId: number) => {
        const checkedItemsAux = checkedItems;

        if (checkedItems.includes(itemId)) {
            const index = checkedItemsAux.findIndex(i => i === itemId);
            checkedItemsAux.splice(index, 1);
        } else {
            checkedItemsAux.push(itemId);
        }

        setCheckedItems(checkedItemsAux);
        handleCheckingHasPendingItems(checkedItemsAux);
        forceRender();
        // eslint-disable-next-line
    }, [checkedItems]);


    const handleCheckingHasPendingItems = useCallback((items: number[]) => {
        if (enterprise === undefined || enterprise.requireMandatoryGovernanceItemsMarked === false || dialogContentProps.requiredItems === false) {
            hasPendingGovernanceItems(false);
            return;
        }

        const item = governanceItems.find(i => i.required && !items.includes(+i.id));
        hasPendingGovernanceItems(item !== undefined);
        // eslint-disable-next-line
    }, [enterprise, dialogContentProps, governanceItems]);


    const dialogActions = useMemo(() => {
        const elements = [
            <ActionButton action='close' onClick={handleClose} />
        ];

        return elements;
        // eslint-disable-next-line
    }, [translate]);

    
    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={translate(dialogContentProps.title)}
                dialogActions={dialogActions}
                className={`dialogactions-spacebetween overflow-inherit`}
                absolute={true}
                dividers={true}
            >
                <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                    {dialogContentProps.beforeGovernanceItemsComponent}

                    <Grid item xs={12}>
                        <GovernanceItemsList 
                            items={governanceItems.map(i => {
                                return {
                                    ...i,
                                    selected: checkedItems.includes(+i.id),
                                    executed: dialogContentProps.requiredItems === true ? undefined : dialogContentProps.governanceItemsExecuted.includes(+i.id),
                                    onClick: () => handleUpdateCheckedItems(+i.id),
                                } as any;
                            })}
                            actions={(item) => {
                                return <Button 
                                    className='governanceitem-action text-color-1'
                                    onClick={() => setSelectedItem(item)}
                                >
                                    <MoreHoriz />
                                </Button> ;
                            }}
                        />
                    </Grid>
                </Grid>
            </Dialog>

            <GovernanceItemDescriptionDialog 
                open={selectedItem !== undefined}
                handleClose={() => setSelectedItem(undefined)}
                governanceItem={selectedItem}
            />
        </>
    );
}

export default GovernanceItemsDialog;